/**
 * Constants for the One Pager Deal Summary editor.
 *
 * Header arrays drive both the column order and the per-row data lookup via
 * `*Display` field names produced by Polus Groovy transformations
 * (`transformOnePagerDirectorOverview`, `transformOnePagerBankingOverview`).
 */

export interface TableHeader {
    /** Field name on the BE-provided record (typically a pre-formatted display field). */
    key: string;
    /** Column heading shown in the One Pager / PDF. */
    label: string;
}

/** DOM IDs for portal mount points inside the editor drawer. */
export const PLATE_TOOLBAR_HOST_ID = 'plate-toolbar-host';
export const HISTORY_PORTAL_TARGET_ID = 'deal-summary-plate-history';
export const COMMENTS_PORTAL_TARGET_ID = 'onepager-comment-container';

/** Highlight applied to "[Data Not Available]" placeholders in builder tables. */
export const DATA_NOT_AVAILABLE_HIGHLIGHT = '#FFD044';

/** Placeholder shown when a builder field has no backing data. */
export const DATA_NOT_AVAILABLE_LABEL = '[Data Not Available]';

/** Muted grey used for secondary text (amounts, captions) in builder tables. */
export const MUTED_TEXT_COLOR = '#9D9D9D';

/** Footnote font size used for caption/note text in builder tables. */
export const FOOTNOTE_FONT_SIZE = '14px';

/** Column widths for the two-column (1:2) label/value layout. */
export const DEFAULT_TWO_COL_1_2_SIZES = [346, 691];

/** Record key for the director name display field, shared by builders. */
export const NAME_DISPLAY_KEY = 'nameDisplay';

/** Column keys for the UW debt-schedule table, in display order. */
export const UW_DEBT_SCHEDULE_DATA_HEADERS = ['lenderName', 'typeOfDebtLoan', 'sanctionedAmount', 'outstandingBalance'];

/** Financial ratio rows for the Financial Ratios section, in display order. */
export const FINANCIAL_RATIO_ROWS = [
    { metricKey: 'Days Payables Outstanding (DPO)', label: 'Days Payables Outstanding (DPO)' },
    { metricKey: 'Days Inventory Outstanding (DIO)', label: 'Days Inventory Outstanding (DIO)' },
    { metricKey: 'Days Sales Outstanding (DSO)', label: 'Days Sales Outstanding (DSO)' },
    { metricKey: 'Cash Conversion Cycle (Days)', label: 'Cash Conversion Cycle (Days)' },
    { metricKey: 'Current Ratio', label: 'Current Ratio' },
    { metricKey: 'Debt to equity', label: 'Debt / Equity' },
    { metricKey: 'Debt / EBITDA', label: 'Debt / EBITDA' },
    { metricKey: 'TOL / TNW', label: 'TOL / TNW' },
    { metricKey: 'DSCR', label: 'Debt Service Coverage Ratio (DSCR)' },
];

/** Maps a ratio metric key to its provisional-data field name. */
export const FINANCIAL_RATIO_PROVISIONAL_KEY_MAP: Record<string, string> = {
    'Days Payables Outstanding (DPO)': 'daysPayablesOutStanding',
    'Days Inventory Outstanding (DIO)': 'daysInventoryOutStanding',
    'Days Sales Outstanding (DSO)': 'daysSalesOutStanding',
    'Cash Conversion Cycle (Days)': 'CashConversionCycleDays',
    'Current Ratio': 'CurrentRatio',
    'Debt to equity': 'DebtToEquity',
    'Debt / EBITDA': 'DebtToEBIDTA',
    'TOL / TNW': 'TOLByTNW',
    DSCR: 'DSCR',
};

/** Ratio metric keys rendered as whole-day counts (no decimals). */
export const FINANCIAL_RATIO_DAYS_METRIC_KEYS = new Set([
    'Days Payables Outstanding (DPO)',
    'Days Inventory Outstanding (DIO)',
    'Days Sales Outstanding (DSO)',
    'Cash Conversion Cycle (Days)',
]);

/**
 * Credit Bureau Summary — 7 columns.
 * Data source: `directorBureauList` (Polus → `transformOnePagerDirectorOverview`).
 */
export const CREDIT_BUREAU_HEADERS: ReadonlyArray<TableHeader> = [
    { key: NAME_DISPLAY_KEY, label: 'Name' },
    { key: 'dpdAccountsDisplay', label: 'DPD Accounts' },
    { key: 'currentOverdueDisplay', label: 'Overdue' },
    { key: 'maxDpdsDisplay', label: 'Max DPD (Days)' },
    { key: 'enquiriesInPast90DaysDisplay', label: 'Enquiries (90D)' },
    { key: 'cibilScoreDisplay', label: 'Bureau Score' },
    { key: 'creditBureauDisplay', label: 'Bureau' },
];

/**
 * Banking Summary — 7 columns.
 * Data source: `bankingOverviewList` (Polus → `transformOnePagerBankingOverview`).
 * The totals row is identified by `isTotalsRow: true` on the entry.
 */
export const BANKING_HEADERS: ReadonlyArray<TableHeader> = [
    { key: NAME_DISPLAY_KEY, label: 'Bank Name' },
    { key: 'accountNumberDisplay', label: 'Account No.' },
    { key: 'closingBalanceDisplay', label: 'Closing Balance' },
    { key: 'closingBalanceDateDisplay', label: 'Closing Balance Date' },
    { key: 'odSanctionLimitDisplay', label: 'OD/CC Sanction Limit' },
    { key: 'transactionsLTMDisplay', label: 'Transactions (LTM)' },
    { key: 'missingDaysDisplay', label: 'Missing Days (LTM)' },
];
